import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Award, Download, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import CatalogPreview from "@/components/CatalogPreview";
import api from "@/lib/api";
import { downloadCatalog } from "@/lib/catalogDownload";

const EMPTY = {
  name: "",
  company: "",
  phone: "",
  email: "",
  profession: "",
  business_city: "",
  state: "",
  web_portfolio: "",
  projects_per_year: "",
  projects_with_doors: "",
  has_current_project: "",
  current_project_location: "",
  current_project_timeframe: "",
  purchasing_mode: "",
  credentials: "",
  notes: "",
};

const PROFESSIONS = [
  "Architect",
  "Interior Designer",
  "General Contractor",
  "Builder",
  "Developer",
  "Door Dealer",
  "Design-Build",
  "Other",
];

const STATES = [
  "California - Los Angeles",
  "California - Santa Clara",
  "Colorado - Denver",
  "Nevada - Las Vegas",
  "Other / Not Listed",
];

const PERKS = [
  ["Trade rates", "Curated pricing reserved for approved trade partners."],
  ["Dedicated concierge", "A single point of contact from spec to delivery."],
  ["Priority lead time", "Production slots reserved for professional projects."],
  ["Full catalog access", "Download the complete Atelier catalog anytime."],
];

export default function ProfessionalPage() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    if (submitted) {
      // Scroll success state into view.
      window.scrollTo({ top: window.innerHeight * 0.6, behavior: "smooth" });
    }
  }, [submitted]);

  const PAGE_TITLE = "Professional Pricing — Atelier Doors by ViewLux";
  const PAGE_DESCRIPTION =
    "Trade pricing, dedicated concierge and priority production for architects, builders, designers and contractors. Apply for the Atelier trade program and download the full catalog.";
  const PAGE_URL = "https://atelierdoors.com/professional";
  const PAGE_IMAGE = "https://atelierdoors.com/og-cover.jpg";

  const triggerCatalogDownload = () => downloadCatalog();

  const handleDownloadClick = () => {
    if (submitted) {
      triggerCatalogDownload();
      toast.success("Catalog downloading…");
      return;
    }
    toast.info("Please complete the professional application first.");
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      // Focus the first empty required field for quick completion.
      setTimeout(() => {
        const first = formRef.current.querySelector(
          '[data-testid="pro-page-name"], [data-testid="pro-page-email"], [data-testid="pro-page-profession"]'
        );
        if (first) first.focus();
      }, 450);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const missing = [];
    if (!form.name.trim()) missing.push("full name");
    if (!form.email.trim()) missing.push("email");
    if (!form.phone.trim()) missing.push("phone");
    if (!form.company.trim()) missing.push("business name");
    if (!form.business_city.trim()) missing.push("business city");
    if (!form.state) missing.push("state / region");
    if (!form.profession) missing.push("business type");
    if (!form.web_portfolio.trim()) missing.push("website / portfolio");
    if (!form.projects_per_year) missing.push("projects per year");
    if (!form.projects_with_doors.trim()) missing.push("projects requiring doors");
    if (!form.has_current_project) missing.push("current Atelier project");
    if (form.has_current_project === "yes") {
      if (!form.current_project_location.trim()) missing.push("project location");
      if (!form.current_project_timeframe.trim()) missing.push("project timeframe");
    }
    if (!form.purchasing_mode) missing.push("purchasing mode");
    if (missing.length) {
      toast.error(`Please answer every question — missing: ${missing.join(", ")}.`);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/pro-application", {
        name: form.name.trim(),
        company: form.company.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        profession: form.profession,
        business_city: form.business_city.trim(),
        state: form.state,
        web_portfolio: form.web_portfolio.trim(),
        projects_per_year: form.projects_per_year,
        projects_with_doors: form.projects_with_doors.trim(),
        has_current_project: form.has_current_project,
        current_project_location: form.current_project_location || null,
        current_project_timeframe: form.current_project_timeframe || null,
        purchasing_mode: form.purchasing_mode,
        credentials: form.credentials || null,
        notes: form.notes || null,
      });
      toast.success("Application received — your catalog is downloading.");
      setSubmitted(true);
      setForm(EMPTY);
      // Auto-download the catalog as a thank-you for applying.
      triggerCatalogDownload();
      // Meta Pixel — track pro application as a custom conversion.
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("trackCustom", "ProApplication");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          "Something went wrong. Please try again in a moment."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-bone flex flex-col" data-testid="professional-page">
      <SEO
        title={PAGE_TITLE}
        description={PAGE_DESCRIPTION}
        image={PAGE_IMAGE}
        url={PAGE_URL}
        type="website"
      />
      <Navbar />

      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 bg-brand-green text-brand-bone overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-[0.1]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 12% 20%, #C39A5A 0%, transparent 55%), radial-gradient(circle at 88% 90%, #C39A5A 0%, transparent 45%)",
          }}
        />
        <div className="relative max-w-[1280px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-end">
          <div className="md:col-span-8">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-brand-gold/20 border border-brand-gold flex items-center justify-center shrink-0">
                <Award size={20} className="text-brand-gold" />
              </div>
              <span className="text-[0.62rem] tracking-[0.4em] uppercase text-brand-gold font-medium">
                Professional Pricing
              </span>
            </div>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 font-serif font-light leading-[1.02] tracking-[-0.02em] text-4xl md:text-5xl lg:text-6xl"
            >
              Exclusive pricing for{" "}
              <span className="italic text-brand-gold">qualified professionals</span>.
            </motion.h1>
            <p className="mt-5 text-brand-bone/80 text-base md:text-lg max-w-2xl leading-relaxed font-light">
              Architects, builders, designers, contractors and trade partners — apply
              to unlock our atelier trade pricing, dedicated concierge and priority
              production. Approval is granted after a short review by our team.
            </p>
          </div>

          <div className="md:col-span-4 flex md:justify-end">
            <button
              type="button"
              onClick={handleDownloadClick}
              className="inline-flex items-center gap-3 px-6 py-3.5 bg-brand-gold text-brand-green text-[0.78rem] tracking-[0.22em] uppercase font-semibold rounded-full hover:bg-brand-bone transition-colors shadow-lg"
              data-testid="pro-page-download-catalog"
              aria-label={submitted ? "Download the catalog" : "Apply first to download the catalog"}
            >
              {submitted ? <Download size={17} /> : <Lock size={15} />}
              {submitted ? "Download Catalog" : "Apply to Download Catalog"}
            </button>
          </div>
        </div>
      </section>

      {/* PERKS */}
      <section className="max-w-[1280px] mx-auto w-full px-6 md:px-10 py-14 md:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {PERKS.map(([title, sub]) => (
            <div
              key={title}
              className="border border-brand-green/15 bg-white/60 px-5 py-6"
              data-testid={`pro-perk-${title.replace(/\W+/g, "-").toLowerCase()}`}
            >
              <div className="flex items-center gap-2 text-brand-gold">
                <CheckCircle2 size={16} />
                <span className="text-[0.62rem] tracking-[0.24em] uppercase font-medium">
                  {title}
                </span>
              </div>
              <p className="mt-3 text-sm text-brand-ink/75 leading-relaxed font-light">
                {sub}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* CATALOG PREVIEW TEASE */}
      <CatalogPreview onCTAClick={handleDownloadClick} />

      {/* APPLICATION FORM */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="mb-8 md:mb-10 text-center">
            <span className="text-[0.62rem] tracking-[0.32em] uppercase text-brand-gold font-medium">
              Apply for Trade Pricing
            </span>
            <h2 className="mt-3 font-serif font-light text-3xl md:text-4xl text-brand-green tracking-[-0.01em]">
              Tell us about your practice.
            </h2>
            <p className="mt-3 text-sm md:text-base text-brand-ink/70 font-light leading-relaxed">
              We&apos;ll review your application and be in touch within 2 business days.
            </p>
          </div>

          {submitted ? (
            <div
              className="border border-brand-gold/40 bg-brand-gold/10 px-6 py-8 md:px-10 md:py-10 text-center"
              data-testid="pro-page-success"
            >
              <div className="w-14 h-14 mx-auto rounded-full bg-brand-gold/20 border border-brand-gold flex items-center justify-center">
                <CheckCircle2 size={26} className="text-brand-gold" />
              </div>
              <h3 className="mt-4 font-serif text-2xl text-brand-green">
                Application received.
              </h3>
              <p className="mt-2 text-sm text-brand-ink/70 max-w-md mx-auto">
                Thank you. Your catalog is downloading. Our trade team will
                review your details and follow up shortly.
              </p>
              <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
                <button
                  onClick={handleDownloadClick}
                  className="btn-primary text-sm"
                  data-testid="pro-page-success-download"
                >
                  <Download size={15} /> Download Catalog Again
                </button>
                <button
                  onClick={() => setSubmitted(false)}
                  className="text-[0.72rem] tracking-[0.24em] uppercase text-brand-green hover:text-brand-gold transition"
                  data-testid="pro-page-submit-another"
                >
                  Submit another →
                </button>
              </div>
            </div>
          ) : (
            <form
              ref={formRef}
              onSubmit={submit}
              className="bg-white/80 border border-brand-green/15 px-6 py-7 md:px-10 md:py-10 space-y-5"
              data-testid="pro-page-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
                <FieldLabel label="Full name" required>
                  <input
                    className="pro-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Sofia Marchetti"
                    required
                    data-testid="pro-page-name"
                  />
                </FieldLabel>
                <FieldLabel label="Email" required>
                  <input
                    type="email"
                    className="pro-input"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="sofia@studio.com"
                    required
                    data-testid="pro-page-email"
                  />
                </FieldLabel>
                <FieldLabel label="Phone" required>
                  <input
                    className="pro-input"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+1 305 555 0142"
                    required
                    data-testid="pro-page-phone"
                  />
                </FieldLabel>
                <FieldLabel label="Business type" required>
                  <select
                    className="pro-input"
                    value={form.profession}
                    onChange={(e) => setForm({ ...form, profession: e.target.value })}
                    required
                    data-testid="pro-page-profession"
                  >
                    <option value="">Select…</option>
                    {PROFESSIONS.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </FieldLabel>
                <FieldLabel label="Business / company name" required>
                  <input
                    className="pro-input"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    placeholder="Marchetti Studio"
                    required
                    data-testid="pro-page-company"
                  />
                </FieldLabel>
                <FieldLabel label="City where you operate" required>
                  <input
                    className="pro-input"
                    value={form.business_city}
                    onChange={(e) => setForm({ ...form, business_city: e.target.value })}
                    placeholder="Miami, FL"
                    required
                    data-testid="pro-page-city"
                  />
                </FieldLabel>
                <FieldLabel label="Nearest showroom / region" required>
                  <select
                    className="pro-input"
                    value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value })}
                    required
                    data-testid="pro-page-state"
                  >
                    <option value="">Select your state / showroom</option>
                    {STATES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </FieldLabel>
                <FieldLabel label="Website · Instagram · Houzz · portfolio" required>
                  <input
                    className="pro-input"
                    value={form.web_portfolio}
                    onChange={(e) => setForm({ ...form, web_portfolio: e.target.value })}
                    placeholder="https://…"
                    required
                    data-testid="pro-page-web-portfolio"
                  />
                </FieldLabel>
                <FieldLabel label="Projects per year (residential / commercial)" required>
                  <select
                    className="pro-input"
                    value={form.projects_per_year}
                    onChange={(e) => setForm({ ...form, projects_per_year: e.target.value })}
                    required
                    data-testid="pro-page-projects"
                  >
                    <option value="">Select</option>
                    <option value="1-5">1–5</option>
                    <option value="6-15">6–15</option>
                    <option value="16-30">16–30</option>
                    <option value="31-50">31–50</option>
                    <option value="50+">50+</option>
                  </select>
                </FieldLabel>
                <FieldLabel label="How many of those require doors?" required>
                  <input
                    className="pro-input"
                    value={form.projects_with_doors}
                    onChange={(e) => setForm({ ...form, projects_with_doors: e.target.value })}
                    placeholder="e.g. 8"
                    required
                    data-testid="pro-page-projects-doors"
                  />
                </FieldLabel>
              </div>

              <div className="pt-4 border-t border-brand-green/10 space-y-4">
                <FieldLabel label="Do you have a project where you are currently considering Atelier doors?" required>
                  <div className="flex gap-3 mt-1" data-testid="pro-page-current-project">
                    {[
                      { v: "yes", label: "Yes" },
                      { v: "no", label: "No / Not yet" },
                    ].map(({ v, label }) => (
                      <label
                        key={v}
                        className={`flex-1 border px-3 py-2 text-[0.78rem] tracking-[0.14em] uppercase cursor-pointer transition-colors ${
                          form.has_current_project === v
                            ? "border-brand-gold bg-brand-gold/10 text-brand-green"
                            : "border-brand-ink/15 text-brand-ink/70 hover:border-brand-gold/60"
                        }`}
                      >
                        <input
                          type="radio"
                          name="has_current_project"
                          value={v}
                          checked={form.has_current_project === v}
                          onChange={(e) =>
                            setForm({ ...form, has_current_project: e.target.value })
                          }
                          className="mr-2 accent-brand-gold"
                          data-testid={`pro-page-current-${v}`}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </FieldLabel>

                {form.has_current_project === "yes" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4" data-testid="pro-page-current-details">
                    <FieldLabel label="Project location" required>
                      <input
                        className="pro-input"
                        value={form.current_project_location}
                        onChange={(e) =>
                          setForm({ ...form, current_project_location: e.target.value })
                        }
                        placeholder="City, state"
                        required
                        data-testid="pro-page-current-location"
                      />
                    </FieldLabel>
                    <FieldLabel label="Estimated ordering timeframe" required>
                      <input
                        className="pro-input"
                        value={form.current_project_timeframe}
                        onChange={(e) =>
                          setForm({ ...form, current_project_timeframe: e.target.value })
                        }
                        placeholder="e.g. Within 30 days · Q2 2026"
                        required
                        data-testid="pro-page-current-timeframe"
                      />
                    </FieldLabel>
                  </div>
                )}

                <FieldLabel label="Purchasing / specifying on behalf of clients?" required>
                  <select
                    className="pro-input"
                    value={form.purchasing_mode}
                    onChange={(e) => setForm({ ...form, purchasing_mode: e.target.value })}
                    required
                    data-testid="pro-page-purchasing-mode"
                  >
                    <option value="">Select…</option>
                    <option value="for_clients">Yes — I purchase / specify on behalf of clients</option>
                    <option value="client_orders_separately">No — the client orders separately</option>
                    <option value="both">Both, depending on the project</option>
                  </select>
                </FieldLabel>

                <FieldLabel label="License / credentials (contractor, architect, resale, etc.)">
                  <textarea
                    rows={2}
                    className="pro-input"
                    value={form.credentials}
                    onChange={(e) => setForm({ ...form, credentials: e.target.value })}
                    placeholder="Optional — license number(s), resale certificate, etc."
                    data-testid="pro-page-credentials"
                  />
                </FieldLabel>

                <FieldLabel label="Additional notes (optional)">
                  <textarea
                    rows={3}
                    className="pro-input"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Anything else we should know about your practice or upcoming projects…"
                    data-testid="pro-page-notes"
                  />
                </FieldLabel>
              </div>

              <div className="pt-3 mt-2 border-t border-brand-green/10 space-y-4">
                <p className="text-[0.66rem] text-brand-ink/55 leading-relaxed italic">
                  <span className="not-italic tracking-[0.22em] uppercase text-brand-ink/70 font-medium mr-1">
                    Legal notice —
                  </span>
                  Submission of this application does not guarantee acceptance
                  into the trade pricing program. Approval is at the sole
                  discretion of the Atelier team, based on our review of your
                  profession, portfolio, and program eligibility criteria. We may
                  request additional documentation, and pricing may be updated or
                  withdrawn at any time.
                </p>
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <p
                    className="inline-flex items-center gap-2 text-[0.72rem] tracking-[0.22em] uppercase font-medium text-brand-green/80"
                    data-testid="pro-page-inline-catalog-hint"
                  >
                    <Download size={15} /> Catalog auto-downloads on apply
                  </p>
                  <button
                    type="submit"
                    className={`btn-primary text-sm px-7 py-3 ${submitting ? "opacity-50 pointer-events-none" : ""}`}
                    disabled={submitting}
                    data-testid="pro-page-submit"
                  >
                    {submitting ? "Submitting…" : "Apply & Download Catalog →"}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </section>

      <Footer />

      <style>{`
        [data-testid="professional-page"] .pro-input {
          width: 100%;
          background: #fff;
          border: 1px solid rgba(23,21,18,0.18);
          padding: 12px 14px;
          color: var(--brand-ink);
          font-size: 14px;
          line-height: 1.4;
          transition: border-color .18s ease, box-shadow .18s ease;
        }
        [data-testid="professional-page"] .pro-input:focus {
          outline: none;
          border-color: #C39A5A;
          box-shadow: 0 0 0 3px rgba(195,154,90,0.15);
        }
        [data-testid="professional-page"] textarea.pro-input { resize: vertical; min-height: 110px; }
        [data-testid="professional-page"] select.pro-input {
          appearance: none;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='%230E2E28' d='M6 8L0 0h12z'/></svg>");
          background-repeat: no-repeat;
          background-position: right 14px center;
          padding-right: 34px;
        }
      `}</style>
    </div>
  );
}

function FieldLabel({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[0.62rem] tracking-[0.24em] uppercase text-brand-ink/60 mb-1.5 flex items-center gap-1">
        {label}
        {required && <span className="text-brand-gold">*</span>}
      </span>
      {children}
    </label>
  );
}
